/**
 * Settings → On-Call (ONCALL-PHONE-01, Seth 2026-09-23: "For on call on the
 * mobile app — maybe we only show same day and sales requests from the on
 * call center. Hold for offices don't need to be shown to the techs.")
 *
 * The answering service sorts every after-hours call into one kind (parse-
 * oncall tags it). This card picks which kinds reach the techs' On-Call tab
 * and for how many days; everything else waits on Today for the office. Backed
 * by org_setting `oncall_phone_rules` — changing it never needs a developer.
 */
import { useState } from "react";
import { Moon, Smartphone } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import {
  ONCALL_CLASS_OPTIONS,
  ONCALL_PHONE_RULES_KEY,
  useOncallPhoneRules,
  type OncallClass,
} from "@/features/team/oncall";

export function OncallPhoneSection() {
  const canManage = useCan("settings.scheduling");
  const rules = useOncallPhoneRules();
  const save = useSaveOrgSetting();
  const [classes, setClasses] = useState<OncallClass[] | null>(null);
  const [days, setDays] = useState<string | null>(null);

  const curClasses = classes ?? rules.classes;
  const curDays = days ?? String(rules.days);
  const dayNum = Math.round(Number(curDays));
  const daysOk = Number.isFinite(dayNum) && dayNum >= 1 && dayNum <= 30;
  const sameSet = (a: OncallClass[], b: OncallClass[]) => a.length === b.length && a.every((k) => b.includes(k));
  const dirty = (classes !== null && !sameSet(classes, rules.classes)) || (days !== null && dayNum !== rules.days);

  const toggle = (k: OncallClass) => {
    const next = curClasses.includes(k) ? curClasses.filter((x) => x !== k) : [...curClasses, k];
    if (next.length === 0) return; // at least one kind — an empty tab helps no one
    setClasses(next);
  };
  const onSave = () =>
    save.mutate(
      { key: ONCALL_PHONE_RULES_KEY, value: { classes: curClasses, days: dayNum } },
      { onSuccess: () => { setClasses(null); setDays(null); } },
    );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <Smartphone className="h-4 w-4 text-slate-400" /> Calls on the Techs&apos; Phones
          <InfoTip label="How after-hours calls are sorted">
            The answering service sorts every after-hours call. Checked kinds show on the On-Call tab of the tech app; the
            rest wait on Today for the office.
          </InfoTip>
        </h4>

        <fieldset className="mt-3 divide-y divide-hairline rounded-lg border border-hairline" disabled={!canManage}>
          <legend className="sr-only">Kinds of calls the techs see</legend>
          {ONCALL_CLASS_OPTIONS.map((o) => {
            const on = curClasses.includes(o.key);
            const last = on && curClasses.length === 1;
            return (
              <label key={o.key} className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-slate-50 has-[:disabled]:cursor-default">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canManage || last}
                  onChange={() => toggle(o.key)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800">{o.label}</span>
                  <span className="block text-[12.5px] text-slate-500">{o.hint}{last ? " At least one kind stays on." : ""}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700">
          <Moon className="h-4 w-4 text-slate-400" />
          <span>Show calls from the last</span>
          <input
            type="number"
            min={1}
            max={30}
            value={curDays}
            disabled={!canManage}
            onChange={(e) => setDays(e.target.value)}
            aria-label="Days a call stays on the phones"
            className="w-16 rounded-md border border-hairline px-2 py-1 text-center text-sm disabled:bg-slate-50"
          />
          <span>days</span>
          <InfoTip label="What happens to older calls">Older calls stay on Today until the office clears them.</InfoTip>
        </div>
        {!daysOk && <p className="mt-1.5 text-[12.5px] font-medium text-red-600">Pick 1 to 30 days.</p>}

        {canManage && (dirty || save.isError) && (
          <div className="mt-3 flex items-center justify-end gap-2">
            {save.isError && <span className="text-[12.5px] font-medium text-red-600">Didn&apos;t save — try again.</span>}
            <button type="button" onClick={() => { setClasses(null); setDays(null); }} className="rounded-md px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100">
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={save.isPending || !daysOk || !dirty}
              className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
